import "react-phone-number-input/style.css";
import PhoneInput, {
  Country,
  parsePhoneNumber,
} from "react-phone-number-input";
import React, { useEffect } from "react";

import styled from "styled-components";

import { Rounded } from "../../theme/Rounded";
import { Flex } from "../Flex";
import { InputProps } from "./InputProps";
import { Spacing } from "../../theme/Spacing";
import { useLocation } from "react-router-dom";

const PhoneInputWrapper = styled(Flex)<{
  valid: boolean;
  $showBorderRadius?: boolean;
}>`
  width: 100%;

  .PhoneInput {
    width: 100%;
  }

  .PhoneInputCountrySelectArrow {
    margin-inline: 0.4rem;
  }

  .PhoneInputInput {
    width: 100%;
    height: 2.3rem;
    border: ${({ theme, valid }) =>
      `${valid ? 1 : 2}px solid ${theme[valid ? "lightgray" : "danger"]}`};
    outline: none;
    font-family: inherit;
    padding: ${Spacing.none} ${Spacing.s};
    ${({ $showBorderRadius }) =>
      $showBorderRadius ? `border-radius: ${Rounded.sm};` : ""}
  }

  .PhoneInputInput::placeholder {
    color: var(--atira-placeholder-color);
    opacity: var(--atira-placeholder-opacity, initial);
  }

  .PhoneInputInput:focus {
    outline-color: var(--atira-focus-border-color) !important;
    border-color: var(--atira-focus-border-color) !important;
  }

  .iti__search-input {
    height: 2rem;
    outline: none;
    font-family: inherit;
  }

  .PhoneInputCountry {
    font-family: inherit;
  }
`;

export const PhoneInputComponent: React.FC<
  InputProps & {
    inputType?: React.HTMLInputTypeAttribute;
    errorMessage?: string;
    value: string;
    disableCountrySelect?: boolean;
  }
> = ({
  inputType,
  errorMessage,
  type,
  onChange,
  value,
  disableCountrySelect,
  ref,
  ...props
}) => {
  // react-phone-number-input requires a strict E.164 string (`+` then only
  // digits) to recognize an initial value - a stored value with so much as a
  // leading/trailing space fails that check silently and renders the field
  // empty, even though the raw value is correct everywhere else (list view,
  // duplicate detection). Trim defensively at this boundary rather than
  // trusting every write path to have stored it clean.
  const trimmedValue = typeof value === "string" ? value.trim() : value;

  const [isValid, setIsValid] = React.useState(true);
  const [defaultCountry, setDefaultCountry] = React.useState<Country>("TR");
  const [selectedCountry, setSelectedCountry] = React.useState<Country>();

  const { pathname } = useLocation();

  const onFetchUserIP = async (callback: ATVoidFunction<Country>) => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();

      callback((data.country_code || "TR") as Country);
    } catch (e) {
      console.log(e);
      callback("TR");
    }
  };

  useEffect(() => {
    onFetchUserIP((countryCode) => {
      setDefaultCountry(countryCode);
    });
  }, []);

  useEffect(() => {
    setIsValid(props.valid || true);
  }, [props.valid]);

  useEffect(() => {
    document.querySelectorAll(".PhoneInputInput").forEach((input) => {
      input.classList.add("atira-theme-input");
    });
  }, []);

  const triggerChange = (phone: string) => {
    onChange?.({
      target: {
        value: phone,
        name: props.name,
      },
      currentTarget: {
        value: phone,
        name: props.name,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const getPastedPhoneNumber = (clipboardText: string) => {
    const sanitizedValue = clipboardText.replace(/[^\d+]/g, "").trim();
    const activeCountry = selectedCountry || defaultCountry;

    if (!sanitizedValue) {
      return undefined;
    }

    const candidates = sanitizedValue.startsWith("+")
      ? [sanitizedValue]
      : sanitizedValue.startsWith("00")
        ? [`+${sanitizedValue.slice(2)}`]
        : /^\d{8,}$/.test(sanitizedValue)
          ? [`+${sanitizedValue}`]
          : [];

    for (const candidate of candidates) {
      const parsedPhoneNumber = parsePhoneNumber(candidate);

      if (parsedPhoneNumber?.country) {
        return parsedPhoneNumber.number;
      }
    }

    if (/^\d{8,}$/.test(sanitizedValue)) {
      const parsedLocalPhoneNumber = parsePhoneNumber(
        sanitizedValue,
        activeCountry,
      );

      if (parsedLocalPhoneNumber?.country) {
        return parsedLocalPhoneNumber.number;
      }
    }

    return undefined;
  };

  return (
    <Flex flexDirection="column" position="relative" gap="s" flex={2}>
      <PhoneInputWrapper
        valid={(trimmedValue as string)?.length > 0 ? isValid : true}
        className={props.dataTestId}
        $showBorderRadius={!pathname.includes("/forms")}
      >
        <PhoneInput
          placeholder={props.placeholder}
          value={trimmedValue}
          style={props.style as React.CSSProperties}
          international
          defaultCountry={defaultCountry}
          countryCallingCodeEditable
          onCountryChange={(country) => {
            setSelectedCountry(country);
          }}
          onChange={(phone) => {
            triggerChange(phone || "");
            // isPossiblePhoneNumber(phone || "") &&
            // formatPhoneNumberIntl(phone || "")
            //   ? setIsValid(true)
            //   : setIsValid(false);
          }}
          countrySelectProps={{ disabled: disableCountrySelect }}
          numberInputProps={{
            style: props.style as React.CSSProperties,
            onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
              const pastedPhoneNumber = getPastedPhoneNumber(
                event.clipboardData.getData("text"),
              );

              if (!pastedPhoneNumber) {
                return;
              }

              event.preventDefault();
              triggerChange(pastedPhoneNumber);
            },
          }}
          ref={ref as React.Ref<any>}
          data-testid={props.dataTestId}
        />
      </PhoneInputWrapper>
    </Flex>
  );
};
